import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";

import { useSite } from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  CostInputsInrange,
  FinancialModelInRangeLease,
  RecursivePartial,
} from "@inrange/building-manager-api-client/models-site";
import { Alert } from "react-bootstrap";
import CollapsibleCard from "../CollapsibleCard";
import { parseAsNumber, roundToDp } from "./utils";

const FinancialPerformanceSpvOutputs = ({
  loading,
  error,
  costInputsInrange,
  inrangeFinancialPerformanceLease,
}: {
  loading: boolean;
  error: boolean;
  costInputsInrange: RecursivePartial<AllowStringForNumbers<CostInputsInrange>>;
  inrangeFinancialPerformanceLease: Partial<FinancialModelInRangeLease>;
}) => {
  const { fetchSiteValues } = useSite({ app: "admin" });
  const irPaybackYears = inrangeFinancialPerformanceLease?.paybackMonths
    ? inrangeFinancialPerformanceLease.paybackMonths / 12
    : 0;
  return (
    <CollapsibleCard
      title={`Multisite SPV results${loading ? " (loading...)" : error ? " (failed to load)" : ""}`}
      className="mt-2"
    >
      <Row className="d-flex flex-wrap align-items-end">
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="irDebtPayback">
            <Form.Label>Debt payback (years)</Form.Label>
            <Form.Control
              disabled
              value={
                inrangeFinancialPerformanceLease.paybackMonths
                  ? roundToDp(irPaybackYears, 2)
                  : "N/A"
              }
              isInvalid={
                irPaybackYears >
                parseAsNumber(
                  costInputsInrange.loanTermYears ??
                    fetchSiteValues.data?.site?.costInputsInrange?.loanTermYears
                )
              }
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="dscrMin">
            <Form.Label>DSCR min achieved</Form.Label>
            <Form.Control
              disabled
              value={
                inrangeFinancialPerformanceLease.dscrMin
                  ? roundToDp(inrangeFinancialPerformanceLease.dscrMin, 4)
                  : "N/A"
              }
              isInvalid={
                !!inrangeFinancialPerformanceLease.dscrMin &&
                inrangeFinancialPerformanceLease.dscrMin <
                  parseAsNumber(
                    costInputsInrange.targetDscrMin ??
                      fetchSiteValues.data?.site?.costInputsInrange
                        ?.targetDscrMin
                  )
              }
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="dscrAvg">
            <Form.Label>DSCR avg achieved</Form.Label>
            <Form.Control
              disabled
              value={
                inrangeFinancialPerformanceLease.dscrAvg
                  ? roundToDp(inrangeFinancialPerformanceLease.dscrAvg, 4)
                  : "N/A"
              }
              isInvalid={
                !!inrangeFinancialPerformanceLease.dscrAvg &&
                inrangeFinancialPerformanceLease.dscrAvg <
                  parseAsNumber(
                    costInputsInrange.targetDscrAvg ??
                      fetchSiteValues.data?.site?.costInputsInrange
                        ?.targetDscrAvg
                  )
              }
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId="llcrMin">
            <Form.Label>LLCR min achieved</Form.Label>
            <Form.Control
              disabled
              value={
                inrangeFinancialPerformanceLease.llcrMin
                  ? roundToDp(inrangeFinancialPerformanceLease.llcrMin, 4)
                  : "N/A"
              }
              isInvalid={
                !!inrangeFinancialPerformanceLease.llcrMin &&
                inrangeFinancialPerformanceLease.llcrMin <
                  parseAsNumber(
                    costInputsInrange.targetLlcrMin ??
                      fetchSiteValues.data?.site?.costInputsInrange
                        ?.targetLlcrMin
                  )
              }
            />
          </Form.Group>
        </Col>
      </Row>
      <Alert variant="secondary">
        Info: These figures are based on modelling the selected sites with P90
        generation, regardless of which forecast probability is set on each
        site.
      </Alert>
    </CollapsibleCard>
  );
};

export default FinancialPerformanceSpvOutputs;
